import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Button, Card, Collapse, Descriptions, Input, InputNumber, Modal, Popconfirm, Radio, Space, Spin, Table, Tag, Tooltip, Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import tmAdminExtService from '../../services/tmAdminExtService';
import { StreamStatusTag, TypeTag, fmtNum, fmtTime, toastErr } from './common';
import { StreamQuickActions, askReason, runOp } from './opsCommon';

const { Text } = Typography;

const HOOK_COLOR = { success: 'green', pending: 'blue', failed: 'orange', gave_up: 'red' };

/**
 * Per-stream admin operations: limit overrides, type switch, sandbox, terminate (billing queue), webhook + deliveries, node sync view.
 * Mount inside the existing stream detail drawer: <StreamOpsPanel streamId={s.id} onChanged={reload} />.
 * showStatusActions adds pause / resume / ban / unban (logged endpoints) — off when the host drawer already has them.
 */
const StreamOpsPanel = ({ streamId, onChanged, showStatusActions = false }) => {
    const [v, setV] = useState(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [ov, setOv] = useState({ hourly: null, monthly: null });
    const [type, setType] = useState('transactional');
    const [postal, setPostal] = useState('');

    const load = useCallback(async () => {
        if (!streamId) return;
        setLoading(true);
        try {
            const r = await tmAdminExtService.streamOps(streamId);
            const d = r?.data || null;
            setV(d);
            if (d) {
                setOv({ hourly: d.overrides.hourly_limit_override, monthly: d.overrides.monthly_limit_override });
                setType(d.stream.type);
                setPostal(d.stream.postal_address || '');
            }
        } catch (e) {
            toastErr(e, '加载失败');
        } finally {
            setLoading(false);
        }
    }, [streamId]);

    useEffect(() => { load(); }, [load]);

    const done = () => { load(); onChanged && onChanged(); };
    const op = async (fn, text) => {
        setBusy(true);
        await runOp(fn, text, done);
        setBusy(false);
    };

    const terminate = () => {
        let confirm = '';
        let reason = '';
        Modal.confirm({
            title: `终止发信流「${v.stream.name}」`,
            okText: '终止',
            okButtonProps: { danger: true },
            content: (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text type="secondary">经计费队列终止订阅，吊销密钥并删除本流专属域名，不可恢复。</Text>
                    <Input placeholder="输入发信流名称确认" onChange={(e) => { confirm = e.target.value; }} />
                    <Input placeholder="原因" maxLength={200} onChange={(e) => { reason = e.target.value; }} />
                </Space>
            ),
            onOk: () => op(() => tmAdminExtService.terminate(v.stream.id, confirm, reason), '已提交终止'),
        });
    };

    if (!v) return <div style={{ textAlign: 'center', padding: 24 }}>{loading ? <Spin /> : null}</div>;

    const s = v.stream;
    const dead = s.status === 'terminated';
    const hooks = v.webhook || {};
    const sync = v.sync || {};

    return (
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
                <TypeTag type={s.type} />
                <StreamStatusTag stream={s} />
                {s.sandbox && <Tag color="gold">沙箱{s.sandbox_locked ? '（锁定）' : ''}</Tag>}
                <Text type="secondary">用户 #{s.user_id} · 订阅 #{s.subscription_id} {s.subscription_status}</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading} />
            </Space>
            {showStatusActions && <StreamQuickActions stream={s} onDone={done} />}

            <Card size="small" title="限额覆盖">
                {!v.overrides_supported && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="迁移 tm_admin_2026_10_20.sql 未执行" />}
                <Space wrap align="end">
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>每小时（套餐 {fmtNum(v.plan.hourly_limit)}，当前生效 {fmtNum(v.budget.hourly_limit)}）</Text><br />
                        <InputNumber min={0} max={10000000} placeholder="跟随套餐" value={ov.hourly} disabled={!v.overrides_supported || dead}
                            onChange={(x) => setOv((o) => ({ ...o, hourly: x ?? null }))} style={{ width: 200 }} />
                    </div>
                    <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>每月（当前 {v.budget.monthly_limit === -1 ? '不限' : fmtNum(v.budget.monthly_limit)}，已用 {fmtNum(v.budget.monthly_used)}）</Text><br />
                        <InputNumber min={0} max={1000000000} placeholder="跟随套餐" value={ov.monthly} disabled={!v.overrides_supported || dead}
                            onChange={(x) => setOv((o) => ({ ...o, monthly: x ?? null }))} style={{ width: 200 }} />
                    </div>
                    <Button type="primary" loading={busy} disabled={!v.overrides_supported || dead}
                        onClick={() => op(() => tmAdminExtService.setOverrides(s.id, { hourly_limit_override: ov.hourly, monthly_limit_override: ov.monthly }), '已保存')}>
                        保存
                    </Button>
                </Space>
            </Card>

            <Card size="small" title="类型与沙箱">
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Space wrap>
                        <Radio.Group value={type} onChange={(e) => setType(e.target.value)} disabled={dead}>
                            <Radio value="transactional">交易</Radio>
                            <Radio value="marketing" disabled={!v.plan.marketing}>
                                <Tooltip title={v.plan.marketing ? null : '套餐不支持营销'}>营销</Tooltip>
                            </Radio>
                        </Radio.Group>
                        {type === 'marketing' && (
                            <Input style={{ width: 320 }} placeholder="发件人实体地址（必填）" value={postal} maxLength={500} onChange={(e) => setPostal(e.target.value)} />
                        )}
                        <Button loading={busy} disabled={dead || (type === s.type && (postal || '') === (s.postal_address || ''))}
                            onClick={() => op(() => tmAdminExtService.setType(s.id, type, type === 'marketing' ? postal : undefined), '已切换')}>
                            应用
                        </Button>
                    </Space>
                    <Space wrap>
                        {!s.sandbox
                            ? <Button disabled={dead} loading={busy} onClick={() => askReason('放回沙箱', '管理员放回沙箱', (r) => op(() => tmAdminExtService.sandbox(s.id, true, r), '已放回沙箱'), false)}>放回沙箱</Button>
                            : <Popconfirm title="解除沙箱？" onConfirm={() => op(() => tmAdminExtService.sandbox(s.id, false), '已解除沙箱')}><Button disabled={dead} loading={busy}>解除沙箱</Button></Popconfirm>}
                        <Button danger disabled={dead} loading={busy} onClick={terminate}>终止发信流</Button>
                    </Space>
                </Space>
            </Card>

            <Collapse size="small" items={[
                {
                    key: 'hook',
                    label: <Space>Webhook {hooks.url ? <Text code style={{ fontSize: 12 }}>{hooks.url}</Text> : <Text type="secondary">未配置</Text>}</Space>,
                    children: (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Space wrap size={4}>
                                {(hooks.events || []).map((e) => <Tag key={e}>{e}</Tag>)}
                                <Tag color={hooks.has_secret ? 'green' : 'default'}>{hooks.has_secret ? '已设签名密钥' : '无密钥'}</Tag>
                                {Object.entries(hooks.deliveries_7d || {}).map(([k, n]) => <Tag key={k} color={HOOK_COLOR[k]}>7天 {k} {n}</Tag>)}
                            </Space>
                            <Table size="small" rowKey="id" pagination={false} dataSource={hooks.recent || []} scroll={{ x: 640 }} columns={[
                                { title: '时间', dataIndex: 'created_at', width: 150, render: fmtTime },
                                { title: '事件', dataIndex: 'event_type', width: 90 },
                                { title: '状态', dataIndex: 'status', width: 80, render: (x) => <Tag color={HOOK_COLOR[x]}>{x}</Tag> },
                                { title: '次数', dataIndex: 'attempts', width: 60 },
                                { title: '结果', key: 'r', render: (_, w) => <Text type="secondary" style={{ fontSize: 12 }}>{w.last_status_code || ''} {w.last_error || ''}</Text> },
                            ]} />
                        </Space>
                    ),
                },
                {
                    key: 'sync',
                    label: <Space>节点同步 <Text type="secondary" style={{ fontSize: 12 }}>组 #{sync.group_id ?? '-'} · 本流最近变更 v{sync.stream_last_change} · 当前 v{sync.version}</Text></Space>,
                    children: (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Table size="small" rowKey="id" pagination={false} dataSource={sync.nodes || []} columns={[
                                { title: '节点', key: 'n', render: (_, n) => `#${n.id} ${n.name}` },
                                { title: '状态', dataIndex: 'status', width: 90 },
                                { title: '权重', dataIndex: 'weight', width: 70 },
                                { title: '已同步', key: 'u', width: 190, render: (_, n) => <Space size={4}>{n.up_to_date ? <Tag color="green">最新</Tag> : <Tag color="orange">待同步</Tag>}<Text type="secondary" style={{ fontSize: 12 }}>v{n.last_sync_version ?? '-'} {fmtTime(n.last_sync_at)}</Text></Space> },
                            ]} />
                            {sync.payload_error && <Alert type="error" showIcon message={sync.payload_error} />}
                            {sync.payload && (
                                <Descriptions size="small" column={2} bordered items={[
                                    { key: 'st', label: '状态', children: `${sync.payload.status}${sync.payload.sandbox ? ' · 沙箱' : ''}` },
                                    { key: 'h', label: '小时 剩余/上限', children: `${fmtNum(sync.payload.hourly_remaining)} / ${fmtNum(sync.payload.hourly_limit)}` },
                                    { key: 'm', label: '月剩余', children: sync.payload.monthly_remaining === -1 ? '不限' : fmtNum(sync.payload.monthly_remaining) },
                                    { key: 'o', label: '允许超额', children: sync.payload.overage_allowed ? '是' : '否' },
                                    { key: 'd', label: '发信域名', span: 2, children: (sync.payload.from_domains || []).map((d) => <Tag key={d.domain}>{d.domain} ({d.dkim_selector})</Tag>) },
                                ]} />
                            )}
                            {sync.payload && (
                                <Collapse ghost size="small" items={[{
                                    key: 'raw', label: '原始下发（密钥已隐藏）',
                                    children: <pre style={{ fontSize: 12, maxHeight: 320, overflow: 'auto', margin: 0 }}>{JSON.stringify(sync.payload, null, 2)}</pre>,
                                }]} />
                            )}
                        </Space>
                    ),
                },
            ]} />
        </Space>
    );
};

export default StreamOpsPanel;
